import React, { useMemo } from "react";
import ReactDOMServer from "react-dom/server";

// region Languages
import useTranslation from "src/translations/useTranslation";
import { CanConsumptionMessages } from "@shared/languages/interfaces";
// endregion Languages
// region Utils
import { sortByNatural, sortByText, sortByValue } from "@utils/data-table-sorting";
import {
  HeatShade,
  MIN_TRAVELS_FOR_RANKING,
  NO_VALUE,
  RANKED_METRICS,
  RankedMetricKey,
  ScaleMap,
  ThresholdMap,
  formatHoursAndMinutes,
  formatMetricValue,
  heatShadeOf,
  qualifiesForRanking,
  vehicleTypeLabel
} from "../can-consumption-metrics";
import { CanConsumptionDashboardVehicle } from "../can-consumption.types";
// endregion Utils
// region Components
import NewDataTable, { DataTableColumns } from "@molecules/NewDataTable";
// endregion Components

interface VehiclePerformanceTableProps {
  vehicles: CanConsumptionDashboardVehicle[];
  scales: ScaleMap;
  thresholds: ThresholdMap;
  minTravels: number;
  decimalSeparator: "." | ",";
}

/**
 * One row of the table: the payload plus the markup of the cells that carry more than a number.
 *
 * The markup travels WITH the row on purpose. The DataTable reads the column accessors only when it
 * draws, so a color that depended on a closure would freeze on the limits of the first render; born
 * inside the row, it is rebuilt whenever the customer moves an alert limit.
 */
interface VehicleRow {
  vehicle: CanConsumptionDashboardVehicle;
  /** Identification, its small-basis badge and the plants answering for the vehicle */
  identificationHtml: string;
  /** Vehicle type translated by its uuid; a dash when the vehicle has no type */
  typeLabel: string;
  typeHtml: string;
  /** Heat-mapped cell of every ranked indicator */
  metricsHtml: { [key in RankedMetricKey]: string };
}

/**
 * Width every heat-mapped rate column asks DataTables for.
 *
 * The heat map paints the whole cell, so columns sized by their own content draw colored blocks of
 * different widths and the five bands stop reading as one scale. `NewDataTable` runs with
 * `autoWidth` on, which is exactly what makes `columns.width` effective: DataTables applies this
 * width while measuring the header and then hands the same size to every one of them.
 *
 * It is a request, not a lock. The value sits above the widest unbreakable word of the headers, so
 * the browser never has to overrule it, and Responsive keeps collapsing whole columns into the
 * expandable line instead of clipping them.
 */
const RATE_COLUMN_WIDTH = "110px";

/** A value with no basis of calculation is a dash, never a zero — and it is drawn as one. */
const valueHtml = (text: string): string => (
  text === NO_VALUE ? ReactDOMServer.renderToString(<span className="no-value">{text}</span>) : text
);

/** Cell of a ranked indicator, painted with the band it falls in. */
const heatCellHtml = (text: string, shade: HeatShade | null): string => (
  text === NO_VALUE
    ? ReactDOMServer.renderToString(<span className="no-value">{text}</span>)
    : ReactDOMServer.renderToString(<span className={shade ? `heat-cell ${shade}` : "heat-cell"}>{text}</span>)
);

/**
 * Performance of every vehicle in the period, with the five-band heat map on the rate columns.
 *
 * A cell stays colorless in two cases, and the legend says so: the vehicle has no value for that
 * indicator, or it ran fewer than `minTravels` travels — a base too small to be ranked or alerted on.
 */
const VehiclePerformanceTable: React.FC<VehiclePerformanceTableProps> = ({
  vehicles, scales, thresholds, minTravels, decimalSeparator
}) => {

  const { t } = useTranslation();

  const rows = useMemo<VehicleRow[]>(() => vehicles.map((vehicle) => {
    const enoughTravels = qualifiesForRanking(vehicle, minTravels);
    const typeLabel = vehicleTypeLabel(vehicle.vehicleTypeId, vehicle.vehicleType, "singular", t);

    return {
      vehicle,
      identificationHtml: ReactDOMServer.renderToString(
        <>
          <span className="vehicle-name">
            {vehicle.identification}
            {!enoughTravels && <span className="low-sample">{t(CanConsumptionMessages.tableLowSample)}</span>}
          </span>
          {vehicle.origins.length > 0 && (
            <span className="vehicle-origins" title={t(CanConsumptionMessages.columnPlants)}>
              {vehicle.origins.join(" · ")}
            </span>
          )}
        </>
      ),
      typeLabel: typeLabel ?? NO_VALUE,
      typeHtml: typeLabel === null
        ? valueHtml(NO_VALUE)
        : ReactDOMServer.renderToString(<span className="vehicle-type">{typeLabel}</span>),
      metricsHtml: RANKED_METRICS.reduce((cells, descriptor) => {
        const value = vehicle[descriptor.key];
        const shade = enoughTravels
          ? heatShadeOf(value, descriptor, scales[descriptor.key], thresholds[descriptor.key])
          : null;

        cells[descriptor.key] = heatCellHtml(formatMetricValue(value, descriptor.fractionDigits, decimalSeparator), shade);

        return cells;
      }, {} as { [key in RankedMetricKey]: string })
    };
  }), [vehicles, scales, thresholds, minTravels, decimalSeparator, t]);

  /**
   * A numeric column: ordered by the number, drawn as the formatted text.
   * Without the raw number the DataTable would order the text and put "1.000" before "9".
   */
  const numberColumn = (
    title: string,
    pick: (vehicle: CanConsumptionDashboardVehicle) => number | null,
    format: (vehicle: CanConsumptionDashboardVehicle) => string
  ): DataTableColumns => ({
    title,
    data: sortByValue<VehicleRow>((row) => pick(row.vehicle), (row) => valueHtml(format(row.vehicle))),
    className: "dt-center",
    filterable: false
  });

  const columns: DataTableColumns[] = [
    {
      title: t(CanConsumptionMessages.columnVehicle),
      data: sortByNatural<VehicleRow>((row) => row.vehicle.identification, (row) => row.identificationHtml),
      filterable: false
    },
    {
      title: t(CanConsumptionMessages.columnVehicleType),
      data: sortByText<VehicleRow>((row) => row.typeLabel, (row) => row.typeHtml),
      filterable: false
    },
    numberColumn(t(CanConsumptionMessages.columnTravels), (v) => v.travelsCount, (v) => String(v.travelsCount)),
    numberColumn(
      t(CanConsumptionMessages.columnVolume),
      (v) => v.volumeM3,
      (v) => formatMetricValue(v.volumeM3, 1, decimalSeparator)
    ),
    numberColumn(
      t(CanConsumptionMessages.columnDistance),
      (v) => v.distanceKm,
      (v) => formatMetricValue(v.distanceKm, 0, decimalSeparator)
    ),
    numberColumn(
      t(CanConsumptionMessages.columnFuel),
      (v) => v.fuelLiters,
      (v) => formatMetricValue(v.fuelLiters, 0, decimalSeparator)
    ),
    numberColumn(
      t(CanConsumptionMessages.columnAvgKmPerTravel),
      (v) => v.avgKmPerTravel,
      (v) => formatMetricValue(v.avgKmPerTravel, 1, decimalSeparator)
    ),
    numberColumn(
      t(CanConsumptionMessages.columnAvgSpeed),
      (v) => v.avgSpeedKmh,
      (v) => formatMetricValue(v.avgSpeedKmh, 1, decimalSeparator)
    ),
    numberColumn(
      t(CanConsumptionMessages.columnAvgTravelTime),
      (v) => v.avgTravelMinutes,
      (v) => formatHoursAndMinutes(v.avgTravelMinutes)
    ),
    // km/L keeps its inverted reading here as well: the accessor hands over the raw number, so the
    // "higher is better" column orders by the same rule as any other — only its colors are mirrored.
    ...RANKED_METRICS.map((descriptor): DataTableColumns => ({
      title: `${t(descriptor.shortLabelKey)} (${t(descriptor.unitKey)})`,
      data: sortByValue<VehicleRow>((row) => row.vehicle[descriptor.key], (row) => row.metricsHtml[descriptor.key]),
      className: "dt-center",
      width: RATE_COLUMN_WIDTH,
      filterable: false
    }))
  ];

  const settings: DataTables.Settings = { order: [[0, "asc"]] };

  return (
    <>
      <div className="panel-table">
        <NewDataTable
          data={rows}
          columns={columns}
          settings={settings}
          showPaging={false}
          showInfo={false}
          scrollHeight=""
        />
      </div>
      <div className="legend">
        <div className="legend-scale">
          <span>{t(CanConsumptionMessages.legendBest)}</span>
          <span className="legend-swatches">
            <i style={{ background: "var(--heat-best)" }} />
            <i style={{ background: "var(--heat-good)" }} />
            <i style={{ background: "var(--heat-neutral)" }} />
            <i style={{ background: "var(--heat-warning)" }} />
            <i style={{ background: "var(--heat-critical)" }} />
          </span>
          <span>{t(CanConsumptionMessages.legendWorst)}</span>
        </div>
        <span>{t(CanConsumptionMessages.legendHigherIsBetter)}</span>
        <span>{t(CanConsumptionMessages.legendNoColor, { minTravels: minTravels ?? MIN_TRAVELS_FOR_RANKING })}</span>
      </div>
    </>
  );
};

export default VehiclePerformanceTable;
